import { DateArg, format, formatDistanceToNow } from "date-fns";
import { id } from "date-fns/locale";
import qs, { ParsedQs } from "qs";

export * from "./contants";
export * from "./format-number";
export * from "./helpers";
export * from "./format-relative-time";

export function isObjectValuesEmpty(obj: any): boolean {
  return (
    !obj ||
    !Object.keys(obj).some((item: any) => {
      if (Array.isArray(obj[item])) {
        return obj[item] && obj[item].length > 0 && obj[item].some((it) => !!it);
      }

      return !!obj[item];
    })
  );
}

export function toCamel(data: any): any {
  if (Array.isArray(data)) {
    return data.map(item => toCamel(item));
  }

  if (data !== null && typeof data === 'object') {
    const transformed: any = {};

    Object.keys(data).forEach((key: string) => {
      const value = data[key];

      const shouldSkip = key.startsWith('__');

      const transformedKey = shouldSkip
        ? key
        : key.replace(/(_\w)/g, m => m[1].toUpperCase());

      const transformedValue =
        value !== null && typeof value === 'object'
          ? toCamel(value)
          : value;

      transformed[transformedKey] = transformedValue;
    });

    return transformed;
  }

  return data;
}

export function toSnake(data: any): any {
  if (Array.isArray(data)) {
    return data.map(item => toSnake(item));
  }

  if (data !== null && typeof data === "object") {
    const transformed: any = {};

    Object.keys(data).forEach((key: string) => {
      const value = data[key];

      const shouldSkip = key.startsWith("__");

      const transformedKey = shouldSkip
        ? key
        : key
          .replace(/([a-z0-9])([A-Z])/g, "$1_$2")
          .replace(/([A-Z])([A-Z][a-z])/g, "$1_$2")
          .toLowerCase();

      const transformedValue =
        value !== null && typeof value === "object"
          ? toSnake(value)
          : value;

      transformed[transformedKey] = transformedValue;
    });

    return transformed;
  }

  return data;
}

export function transformQueryParams(path: string, params?: Record<any, any> | null): string {
  const _query = qs.stringify(
    {
      locale: "id-ID",
      ...params,
    },
    { addQueryPrefix: true, encodeValuesOnly: true, skipNulls: true },
  );

  return `${path}${_query}`;
}

export function transformQueryParamsWithoutLocale(path: string, params?: Record<any, any> | null): string {
  const _query = qs.stringify(
    {
      ...params,
    },
    { addQueryPrefix: true, encodeValuesOnly: true, skipNulls: true },
  );

  return `${path}${_query}`;
}

export function transformQueryParamsStringToObj(str: string): ParsedQs {
  return qs.parse(str || "");
}

export function formatDateValue(val: number | string | DateArg<Date>): Date {
  let date: Date;

  if (typeof val === "number") {
    date = new Date(val * 1000);
  } else {
    date = new Date(val);
  }

  return date;
}

export function formatRelativeTime(dateInput: number | string): string {
  const date = formatDateValue(dateInput);

  if (isNaN(date.getTime())) {
    return "-";
  }

  return formatDistanceToNow(date, { addSuffix: true, locale: id });
}

export function formatCurrency(value: number, currency?: string, notation?: "standard" | "compact") {
  return new Intl.NumberFormat("id-ID", {
    style: "currency",
    currency: currency || "IDR",
    notation: notation,
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(value);
}

export function formatDate(date: DateArg<Date>, dateFormat: string = "dd MMM yyyy") {
  const val = formatDateValue(date);

  return format(val, dateFormat, { locale: id });
}

export function formatDateTime(dateTime: DateArg<Date>, dateTimeFormat: string = "dd MMM yyyy HH:mm") {
  const val = formatDateValue(dateTime);
  return format(val, dateTimeFormat, { locale: id });
}

export function transformToSlug(str: string, combine?: string): string {
  const slug = str.replace(/\//g, '').replace(/\(/g, '').replace(/\)/g, '').replace(/ /g, '-').toLowerCase();
  if (slug && combine) {
    return slug + '-' + combine;
  }

  return slug;
}

export function getIdFromSlug(slug: string): string | null {
  const regex: RegExp = new RegExp(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-5][0-9a-f]{3}-[089ab][0-9a-f]{3}-[0-9a-f]{12}$/i);

  if (regex.test(slug)) {
    return slug;
  }

  const uuid = slug.slice(slug.length - 36);
  if (uuid) {
    return uuid;
  }

  return null;
}

export function cssStringToObject(style?: string | Record<string, any>) {
  if (!style) return undefined;
  if (typeof style === "object") return style;

  const obj: Record<string, string> = {};
  style.split(";").forEach((decl) => {
    const [rawProp, rawVal] = decl.split(":");
    if (!rawProp || !rawVal) return;
    const prop = rawProp.trim();
    const val = rawVal.trim();
    const camel = prop.replace(/-([a-z])/g, (_, c) => c.toUpperCase());
    obj[camel] = val;
  });
  return obj;
}

export function mapAttribsToProps(attribs: Record<string, any> = {}) {
  const props: Record<string, any> = { ...attribs };

  if (props.class) {
    props.className = props.class;
    delete props.class;
  }

  if (props.style && typeof props.style === "string") {
    props.style = cssStringToObject(props.style);
  }

  const aliasMap: Record<string, string> = {
    colspan: "colSpan",
    rowspan: "rowSpan",
    for: "htmlFor",
    readonly: "readOnly",
    tabindex: "tabIndex",
  };

  Object.entries(aliasMap).forEach(([k, v]) => {
    if (props[k] !== undefined) {
      props[v] = props[k];
      delete props[k];
    }
  });

  return props;
}